import moment from 'moment'
import { useContext, useEffect, useRef } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { CardApplicationUI } from './ui'
import { CardApplicationTabPrimaryUI } from './ui.tab.primary'
import { CardApplicationTabHelpUI } from './ui.tab.help'
import { CardApplicationTabLogsUI } from './ui.tab.logs'
import { 
    ICardApplicationContainer, ICardApplicationUI, IFieldsError, FieldsErrorType,
    ICardApplicationTabPrimaryUI, ICardApplicationTabHelpUI, ICardApplicationTabLogsUI,
} from './types'

import { useCommon } from '@api/common/state'
import { useDict } from '@api/dict/state'
import { ISitesDbNameList } from '@api/dict/state.types'
import { useApplications } from '@api/applications/state'
import { IApplicationCard, IApplicationCardDisciplines, IApplicationCardLogs, IApplicationCardPrograms } from '@api/applications/state.types'

import { useValue } from '@hooks/useValue'
import { useCardSaveGuard } from '@hooks/useCardSaveGuard'
import { useFieldsErrorAnimation } from '@hooks/useFieldsErrorAnimation'
import { phoneMask, validFormData } from '@hooks/useForm'
import { useDebounce } from '@hooks/useDebounce'
import { reportUnexpectedClientError } from '@services/client-error.service'

import { IModalNotice, ModalNotice } from '@primitive/modal.notice'
import { IModalConfirm, ModalConfirm } from '@primitive/modal.confirm'

import { DictNameType } from '@smart/dictSelector/types'
import { IModalApplicationMailingsContainer, ModalApplicationMailingsContainer } from '@smart/modalApplicationMailings'
import { CardModeType } from '@smart/common.type'

import { NotificationContext, notifyOpen } from '@ui/notification/notification.provider'


type ApplicationFormState = {
    dbName: string
    clientName: string
    clientPhone: string
    clientEmail: string
    clientRating: number
    programNameID: number | ''
    programName: string
    programTypeID: number | ''
    programType: string
    attestationTypeID: number | ''
    educationDateStart: string
    educationDateEnd: string
    durationEducation: string
    volumeHours: string
    programSum: string
    applicationStatusID: number | ''
    applicationTypeID: number | ''
    applicationSourceID: number | ''
    dateNextCall: string
    timeNextCall: string
    applicationComment: string
}

const DEFAULT_APPLICATION_FORM_STATE: ApplicationFormState = {
    dbName: 'modern_academy',
    clientName: '',
    clientPhone: '',
    clientEmail: '',
    clientRating: 5,
    programNameID: '',
    programName: '',
    programTypeID: '',
    programType: '',
    attestationTypeID: 2,
    educationDateStart: '',
    educationDateEnd: '',
    durationEducation: '',
    volumeHours: '',
    programSum: '',
    applicationStatusID: 1,
    applicationTypeID: 1,
    applicationSourceID: 1,
    dateNextCall: '',
    timeNextCall: '',
    applicationComment: '',
}

const createApplicationFormSnapshot = (state: ApplicationFormState) => JSON.stringify(state)
const createEducationSignature = (durationEducation: string, programTypeID: number | '', educationDateStart: string) => JSON.stringify([durationEducation, programTypeID, educationDateStart])

const getApplicationFormState = (card: IApplicationCard): ApplicationFormState => ({
    dbName: card.dbName || 'modern_academy',
    clientName: card.clientName || '',
    clientPhone: card.clientPhone || '',
    clientEmail: card.clientEmail || '',
    clientRating: card.clientRating || 0,
    programNameID: card.programNameID || '',
    programName: card.programName || '',
    programTypeID: card.programTypeID || '',
    programType: card.programType || '',
    attestationTypeID: card.attestationTypeID || '',
    educationDateStart: card.educationDateStart || '',
    educationDateEnd: card.educationDateEnd || '',
    durationEducation: card.durationEducation || '',
    volumeHours: card.volumeHours || '',
    programSum: card.programSum || '',
    applicationStatusID: card.applicationStatusID || '',
    applicationTypeID: card.applicationTypeID || '',
    applicationSourceID: card.applicationSourceID || '',
    dateNextCall: card.dateNextCall || '',
    timeNextCall: card.timeNextCall || '',
    applicationComment: card.applicationComment || '',
})

/** Контейнер - Анкета заявки */
export const CardApplicationContainer = ({ isOpen, cardID, cardMode, handleButtonOk, handleButtonCancel }: ICardApplicationContainer) => {
    const notify = useContext(NotificationContext)

    const modalMailings = useValue<IModalApplicationMailingsContainer>({isOpen: false})
    const modalConfirm = useValue<IModalConfirm>({isOpen: false})
    const modalNotice = useValue<IModalNotice>({isOpen: false})

    const tabActive = useValue<string>('primary')
    const fieldsError = useValue<IFieldsError>({})
    const currentCardID = useValue<number>(0)
    const currentCardMode = useValue<CardModeType>('new')
    const isDisableBtnSave = useValue<boolean>(true)
    const savedFormSnapshotRef = useRef(createApplicationFormSnapshot(DEFAULT_APPLICATION_FORM_STATE))
    const savedEducationSignatureRef = useRef(createEducationSignature('', '', ''))
    const hasSavedChangesRef = useRef(false)
    const saveGuard = useCardSaveGuard(isOpen, `${cardMode}:${cardID}`, () => {
        hasSavedChangesRef.current = true
        notify && notifyOpen('Результат сохранения не подтверждён. Анкета могла быть создана. Повторное создание заблокировано; закройте анкету и проверьте список.', 'warning', 10000, notify)
    })
    const isFormReadyRef = useRef(false)
    const isFieldsDisabled = useValue<boolean>(false)
    const dbNameList = useValue<ISitesDbNameList[]>([])
    const dbNameSelected = useValue<string>('modern_academy')
    const dataBaseName = useValue<string>('')
    
    const applicationNumber = useValue<number | ''>('')
    const applicationDateCreate = useValue<string>('')
    const applicationIsRepeat = useValue<number | ''>('')
    const applicationOrigin = useValue<string>('')
    
    const txtClientName = useValue<string>('')
    const txtClientPhone = useValue<string>('')
    const txtClientEmail = useValue<string>('')
    const txtClientRating = useValue<number>(5)
    
    const txtProgramNameID = useValue<number | ''>('')
    const txtProgramName = useValue<string>('')
    const txtProgramTypeID = useValue<number | ''>('')
    const txtProgramType = useValue<string>('')
    const txtAttestationTypeID = useValue<number | ''>(2)
    const txtAttestationType = useValue<string>('Итоговое тестирование')
    const txtEducationDateStart = useValue<string>('')
    const txtEducationDateEnd = useValue<string>('')    
    const txtDurationEducation = useValue<string>('')
    const txtVolumeHours = useValue<string>('')
    const txtProgramSum = useValue<string>('')
    
    const txtApplicationStatusID = useValue<number | ''>(1)
    const txtApplicationStatus = useValue<string>('Новая')
    const txtDateNextCall = useValue<string>('')
    const txtTimeNextCall = useValue<string>('')
    const txtApplicationTypeID = useValue<number | ''>(1)
    const txtApplicationType = useValue<string>('Заявка')
    const txtApplicationSourceID = useValue<number | ''>(1)
    const txtApplicationSource = useValue<string>('Телефон')
    const txtApplicationComment = useValue<string>('')

    const programsList = useValue<IApplicationCardPrograms[]>([])
    const disciplinesPP = useValue<IApplicationCardDisciplines[]>([])
    const disciplinesPK = useValue<IApplicationCardDisciplines[]>([])
    const logsList = useValue<IApplicationCardLogs[]>([])
    const countDisciplineSectionPP = useValue<number[]>([])
    const countDisciplineSectionPK = useValue<number[]>([])

    const { actionGetEduDateStartEnd } = useCommon(useShallow((state) => state))

    const { 
        isLoadingCard, actionGetApplicationCard, actionSetApplicationCard, actionCreateDemoAccess, actionGetApplicationCheckPhone, actionDownloadFile
    } = useApplications(useShallow((state) => ({
        isLoadingCard: state.isLoadingCard,
        actionGetApplicationCard: state.actionGetApplicationCard,
        actionSetApplicationCard: state.actionSetApplicationCard,
        actionCreateDemoAccess: state.actionCreateDemoAccess,
        actionGetApplicationCheckPhone: state.actionGetApplicationCheckPhone,
        actionDownloadFile: state.actionDownloadFile,
    })))

    const {clearDictState, actionGetSitesDbName} = useDict(useShallow(state => state))

    // ================================================================================

    const getCurrentFormState = (): ApplicationFormState => ({
        dbName: dbNameSelected.value,
        clientName: txtClientName.value,
        clientPhone: txtClientPhone.value,
        clientEmail: txtClientEmail.value,
        clientRating: txtClientRating.value,
        programNameID: txtProgramNameID.value,
        programName: txtProgramName.value,
        programTypeID: txtProgramTypeID.value,
        programType: txtProgramType.value,
        attestationTypeID: txtAttestationTypeID.value,
        educationDateStart: txtEducationDateStart.value,
        educationDateEnd: txtEducationDateEnd.value,
        durationEducation: txtDurationEducation.value,
        volumeHours: txtVolumeHours.value,
        programSum: txtProgramSum.value,
        applicationStatusID: txtApplicationStatusID.value,
        applicationTypeID: txtApplicationTypeID.value,
        applicationSourceID: txtApplicationSourceID.value,
        dateNextCall: txtDateNextCall.value,
        timeNextCall: txtTimeNextCall.value,
        applicationComment: txtApplicationComment.value,
    })

    const applyCardData = (card: IApplicationCard) => {
        const formState = getApplicationFormState(card)

        currentCardID.setValue(card.applicationNumber)
        currentCardMode.setValue('edit')
        dataBaseName.setValue(formState.dbName)
        dbNameSelected.setValue(formState.dbName)
        applicationNumber.setValue(card.applicationNumber)
        applicationDateCreate.setValue(moment(card.applicationDateCreate).format('DD.MM.YYYY'))
        applicationIsRepeat.setValue(card.applicationIsRepeat)
        applicationOrigin.setValue(card.applicationOrigin || '')
        txtClientName.setValue(formState.clientName)
        txtClientPhone.setValue(formState.clientPhone)
        txtClientEmail.setValue(formState.clientEmail)
        txtClientRating.setValue(formState.clientRating)
        txtProgramNameID.setValue(formState.programNameID)
        txtProgramName.setValue(formState.programName)
        txtProgramTypeID.setValue(formState.programTypeID)
        txtProgramType.setValue(formState.programType)
        txtAttestationTypeID.setValue(formState.attestationTypeID)
        txtAttestationType.setValue(card.attestationType || '')
        txtEducationDateStart.setValue(formState.educationDateStart)
        txtEducationDateEnd.setValue(formState.educationDateEnd)
        txtDurationEducation.setValue(formState.durationEducation)
        txtVolumeHours.setValue(formState.volumeHours)
        txtProgramSum.setValue(formState.programSum)
        txtApplicationStatusID.setValue(formState.applicationStatusID)
        txtApplicationStatus.setValue(card.applicationStatus || '')
        txtDateNextCall.setValue(formState.dateNextCall)
        txtTimeNextCall.setValue(formState.timeNextCall)
        txtApplicationTypeID.setValue(formState.applicationTypeID)
        txtApplicationType.setValue(card.applicationType || '')
        txtApplicationSourceID.setValue(formState.applicationSourceID)
        txtApplicationSource.setValue(card.applicationSource || '')
        txtApplicationComment.setValue(formState.applicationComment)
        programsList.setValue(card.queryPrograms)
        disciplinesPP.setValue(card.queryDisciplinesPP)
        disciplinesPK.setValue(card.queryDisciplinesPK)
        logsList.setValue(card.logs)
        countDisciplineSectionPP.setValue(Array.from(new Set(card.queryDisciplinesPP.map((item) => item.volume_hours))))
        countDisciplineSectionPK.setValue(Array.from(new Set(card.queryDisciplinesPK.map((item) => item.volume_hours))))

        savedFormSnapshotRef.current = createApplicationFormSnapshot(formState)
        savedEducationSignatureRef.current = createEducationSignature(formState.durationEducation, formState.programTypeID, formState.educationDateStart)
        isFormReadyRef.current = true
        isDisableBtnSave.setValue(true)
    }

    useEffect(() => {
        let isCurrentRequest = true

        if (isOpen) {
            clearCard()
            const requestedCardMode = cardMode || 'new'
            const requestedCardID = Number(cardID) || 0
            currentCardID.setValue(requestedCardID)
            currentCardMode.setValue(requestedCardMode)

            actionGetSitesDbName().then((res) => {
                if (!isCurrentRequest) return
                dbNameList.setValue(res)
                if (requestedCardMode === 'new') {
                    const selectedDbName = res[0]?.siteDbName || DEFAULT_APPLICATION_FORM_STATE.dbName
                    dbNameSelected.setValue(selectedDbName)
                    savedFormSnapshotRef.current = createApplicationFormSnapshot({...DEFAULT_APPLICATION_FORM_STATE, dbName: selectedDbName})
                    isFormReadyRef.current = true
                    isDisableBtnSave.setValue(true)
                }
            })

            requestedCardMode === 'edit' && actionGetApplicationCard(requestedCardID).then((res) => {
                if (!isCurrentRequest) return
                if (res.status === 200 && res.data) applyCardData(res.data)
            })
        }

        !isOpen && clearCard() // Обнуление данных по анкете

        return () => {
            isCurrentRequest = false
        }
    }, [isOpen, cardID, cardMode]) // eslint-disable-line react-hooks/exhaustive-deps

    const currentFormSnapshot = createApplicationFormSnapshot(getCurrentFormState())
    const setIsDisableBtnSave = isDisableBtnSave.setValue

    useEffect(() => {
        if (!isOpen || !isFormReadyRef.current) {
            setIsDisableBtnSave(true)
            return
        }

        setIsDisableBtnSave(currentFormSnapshot === savedFormSnapshotRef.current)
    }, [isOpen, currentFormSnapshot, setIsDisableBtnSave])

    useEffect(() => {
        isFieldsDisabled.setValue(!txtProgramNameID.value)
    }, [txtProgramNameID.value]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!isOpen || !isFormReadyRef.current) return

        const educationSignature = createEducationSignature(txtDurationEducation.value, txtProgramTypeID.value, txtEducationDateStart.value)
        if (educationSignature === savedEducationSignatureRef.current) return

        getEducationDate(txtEducationDateStart.value)
    }, [isOpen, txtDurationEducation.value, txtProgramTypeID.value]) // eslint-disable-line react-hooks/exhaustive-deps
    
    // ================================================================================

    /** Сброс всех данных по текущей анкете */
    const clearCard = () => {
        clearDictState()

        tabActive.setValue('primary')
        fieldsError.setValue({})
        currentCardID.setValue(0)
        currentCardMode.setValue('new')
        isDisableBtnSave.setValue(true)
        isFormReadyRef.current = false
        hasSavedChangesRef.current = false
        savedFormSnapshotRef.current = createApplicationFormSnapshot(DEFAULT_APPLICATION_FORM_STATE)
        savedEducationSignatureRef.current = createEducationSignature('', '', '')
        isFieldsDisabled.setValue(false)
        dbNameList.setValue([])
        dbNameSelected.setValue('modern_academy')
        dataBaseName.setValue('')
    
        applicationNumber.setValue('')
        applicationDateCreate.setValue('')
        applicationIsRepeat.setValue('')
        applicationOrigin.setValue('')
    
        txtClientName.setValue('')
        txtClientPhone.setValue('')
        txtClientEmail.setValue('')
        txtClientRating.setValue(5)
        
        txtProgramNameID.setValue('')
        txtProgramName.setValue('')
        txtProgramTypeID.setValue('')
        txtProgramType.setValue('')
        txtAttestationTypeID.setValue(2)
        txtAttestationType.setValue('Итоговое тестирование')
        txtEducationDateStart.setValue('')
        txtEducationDateEnd.setValue('')    
        txtDurationEducation.setValue('')
        txtVolumeHours.setValue('')
        txtProgramSum.setValue('')
        
        txtApplicationStatusID.setValue(1)
        txtApplicationStatus.setValue('Новая')
        txtDateNextCall.setValue('')
        txtTimeNextCall.setValue('')
        txtApplicationTypeID.setValue(1)
        txtApplicationType.setValue('Заявка')
        txtApplicationSourceID.setValue(1)
        txtApplicationSource.setValue('Телефон')
        txtApplicationComment.setValue('')

        programsList.setValue([])
        disciplinesPP.setValue([])
        disciplinesPK.setValue([])
        logsList.setValue([])

        countDisciplineSectionPP.setValue([])
        countDisciplineSectionPK.setValue([])
    }

    /** Получение активной вкладки */
    const getCardPage = (tabActive: string) => {
        switch (tabActive) {
            case 'primary': return <CardApplicationTabPrimaryUI {...propsTabPrimaryUI} />
            case 'help': return <CardApplicationTabHelpUI {...propsTabHelpUI} />
            case 'logs': return <CardApplicationTabLogsUI {...propsTabLogsUI} />
        }
    }

    /** Получение дат, начала обучения и окончание обучения */
    const getEducationDate = (eduDateStart: string) => {
        actionGetEduDateStartEnd(eduDateStart, Number(txtDurationEducation.value), txtProgramTypeID.value === 2 ? 1 : 0).then((res) => {
            res.eduStartDate && txtEducationDateStart.setValue(res.eduStartDate)
            res.eduEndDate && txtEducationDateEnd.setValue(res.eduEndDate)
        })
    }

    /** Проверка по номеру телефона, существование заявки */
    const applicationCheckPhoneNumber = async (phoneNumber: string) => {
        const res = await actionGetApplicationCheckPhone(currentCardID.value, phoneNumber)
        if (res?.data?.result) handlerOpenModalNotice(true, 'Извещение...', `<b>Внимание!</b><br/>Заявка с таким номером телефона уже зарегистрирована, ее номер: <b>${res.data.appID}</b>`)
    }

    /** При выборе данных из справочника */
    const dictSelectorData = (dictName: DictNameType, data: {id: number, name: string, param1?: string, param2?: string, param3?: string, param4?: string, param5?: string, param6?: string}) => {
        switch (dictName) {
            case 'courses': {
                txtProgramNameID.setValue(Number(data.id)) // Идентификатор программы
                txtProgramName.setValue(data.name) // Наименование программы

                txtProgramTypeID.setValue(Number(data.param2)) // Идентификатор типа программы
                txtProgramType.setValue(data.param3 || '') // Наименование типа программы

                txtDurationEducation.setValue(data.param4 || '') // Продолжительность
                txtVolumeHours.setValue(data.param5 || '') //  Обьем программы
                txtProgramSum.setValue(data.param6 || '') // Стоимость программы
                break
            }

            case 'programType': {
                txtProgramTypeID.setValue(Number(data.id)) // Идентификатор типа программы
                txtProgramType.setValue(data.name) // Наименование типа программы

                txtAttestationTypeID.setValue(Number(data.param2)) // Идентификатор типа аттестации
                txtAttestationType.setValue(data.param3 || '') // Идентификатор типа аттестации

                txtDurationEducation.setValue(data.param4 || '') // Продолжительность
                txtVolumeHours.setValue(data.param5 || '') //  Обьем программы
                txtProgramSum.setValue(data.param6 || '') // Стоимость программы
                break
            }

            case 'attestationType': {
                txtAttestationTypeID.setValue(Number(data.id)) // Идентификатор типа аттестации
                txtAttestationType.setValue(data.name) // Идентификатор типа аттестации
                break
            }

            case 'volumeHours': {
                txtDurationEducation.setValue(data.param1 || '') // Продолжительность
                txtVolumeHours.setValue(data.name) //  Обьем программы
                txtProgramSum.setValue(data.param2 || '') // Стоимость программы
                break
            }

            case 'applicationStatus': {
                txtApplicationStatusID.setValue(Number(data.id))
                txtApplicationStatus.setValue(data.name)
                break
            }

            case 'applicationType': {
                txtApplicationTypeID.setValue(Number(data.id))
                txtApplicationType.setValue(data.name)
                break
            }

            case 'applicationSource': {
                txtApplicationSourceID.setValue(Number(data.id))
                txtApplicationSource.setValue(data.name)
                break
            }

        }
    }

    // ================================================================================

    /** Открытие/закрытие окна выбора рассылок в telegram */
    const handleOpenModalMailings = (isOpen: boolean) => {
        if (!currentCardID.value) return notify && notifyOpen('Идентификактор заявки еще не определён. Возможно вы ещё не создали заявку. Открытие окна рассылок отколнено.', 'warning', 2000, notify)

        modalMailings.setValue({
            isOpen, applicationID: currentCardID.value,
            handleButtonCancel() { modalMailings.setValue({ isOpen: false }) },
        })
    }
    /** Открытие/закрытие окна подтверждения */
    const handleOpenModalConfirm = (isOpen: boolean, headerText: string, contentText: string) => {
        if (!currentCardID.value) return notify && notifyOpen('Идентификактор заявки еще не определён. Возможно вы ещё не создали заявку. Создание демо-доступа отклонено.', 'warning', 2000, notify)
        
        modalConfirm.setValue({
            isOpen, headerText, contentText, 
            handleButtonCancel() {modalConfirm.setValue({isOpen: false})}, 
            async handleButtonOk() {
                modalConfirm.setValue((currentValue) => ({...currentValue, isLoading: true}))

                try {
                    const response = await actionCreateDemoAccess(currentCardID.value)

                    if (response.status === 200) {
                        notify && notifyOpen('Демо-доступ создан, данные отправлены в Telegram.', 'success', 2000, notify)
                    } else {
                        notify && notifyOpen(response.message, response.status >= 500 ? 'error' : 'warning', 3000, notify)
                    }
                } catch (error) {
                    reportUnexpectedClientError(error, 'CardApplication.actionCreateDemoAccess')
                    notify && notifyOpen('Не удалось создать демо-доступ. Повторите попытку.', 'error', 3000, notify)
                } finally {
                    modalConfirm.setValue({isOpen: false})
                }
            }
        })
    }
    /** Открытие/закрытие окна извещения */
    const handlerOpenModalNotice = (isOpen: boolean, headerText: string, contentText: string) => {
        modalNotice.setValue({
            isOpen, headerText, contentText, 
            handleButtonOk() {modalNotice.setValue({isOpen: false})}, 
        })
    }
    const closeCard = () => {
        modalConfirm.setValue({isOpen: false})

        if (hasSavedChangesRef.current) {
            handleButtonOk && handleButtonOk()
        } else {
            handleButtonCancel && handleButtonCancel()
        }
    }
    const handleBtnClose = () => {
        if (isLoadingCard || modalConfirm.value.isOpen) return

        if (isDisableBtnSave.value) {
            closeCard()
            return
        }

        modalConfirm.setValue({
            isOpen: true,
            headerText: 'Сохранение формы',
            contentText: '<b>Внимание!</b> Имеются не сохранённые данные!<br/><br/>Вы действительно хотите закрыть текущее окно, без сохранения?',
            cancelButtonText: 'Продолжить редактирование',
            okButtonText: 'Закрыть без сохранения',
            handleButtonCancel() {modalConfirm.setValue({isOpen: false})},
            handleButtonOk() {closeCard()},
        })
    }



    /** Кнопка сохранить все данные во вкладке primary */
    const handleBtnSave = (isClosedModal: boolean) => {
        const validateData = validFormData([ // Валидируем данные 
            // {fieldName: 'clientName', value: txtClientName.value, validType: 'notEmpty', isRequired: true, errorMessage: 'Имя клиента должно быть обязательно заполнено.'},
            {fieldName: 'clientPhone', value: txtClientPhone.value, validType: 'phoneMask', isRequired: false, errorMessage: 'Номер телефона не корректный.'},
            {fieldName: 'clientEmail', value: txtClientEmail.value, validType: 'email', isRequired: false, errorMessage: 'Электронная почта не корректная.'},

            {fieldName: 'programName', value: txtProgramName.value, validType: 'notEmpty', isRequired: [1, 4, 5, 7].includes(txtApplicationStatusID.value || 0) ? false : true, errorMessage: 'Наименование программы, обязательно должно быть указано.'},
            {fieldName: 'programType', value: txtProgramType.value, validType: 'notEmpty', isRequired: [1, 4, 5, 7].includes(txtApplicationStatusID.value || 0) ? false : true, errorMessage: 'Тип программы, обязательно должен быть указан.'},
            {fieldName: 'attestationType', value: txtAttestationType.value, validType: 'notEmpty', isRequired: [1, 4, 5, 7].includes(txtApplicationStatusID.value || 0) ? false : true, errorMessage: 'Тип аттестации, обязательно должен быть указан.'},
            
            {fieldName: 'educationDateStart', value: txtEducationDateStart.value, validType: 'date', isRequired: [1, 4, 5, 7].includes(txtApplicationStatusID.value || 0) ? false : true, errorMessage: 'Дата начала обучения не корректна или такой даты не существует.'},
            {fieldName: 'educationDateEnd', value: txtEducationDateEnd.value, validType: 'date', isRequired: [1, 4, 5, 7].includes(txtApplicationStatusID.value || 0) ? false : true, errorMessage: 'Дата окончания обучения не корректна или такой даты не существует.'},
            
            {fieldName: 'durationEducation', value: txtDurationEducation.value, validType: 'numberDecimal', isRequired: [1, 4, 5, 7].includes(txtApplicationStatusID.value || 0) ? false : true, errorMessage: 'Продолжительность программы, обязательно должна быть указана.'},
            {fieldName: 'volumeHours', value: txtVolumeHours.value, validType: 'number_1-9', isRequired: [1, 4, 5, 7].includes(txtApplicationStatusID.value || 0) ? false : true, errorMessage: 'Объём программы, обязательно должен быть указан.'},
            {fieldName: 'programSum', value: txtProgramSum.value, validType: 'number_1-9', isRequired: [1, 4, 5, 7].includes(txtApplicationStatusID.value || 0) ? false : true, errorMessage: 'Стоимость программы не корректное значение.'},

            {fieldName: 'applicationStatus', value: txtApplicationStatus.value, validType: 'notEmpty', isRequired: true, errorMessage: 'Статус заявки, обязательно должен быть указан.'},
            {fieldName: 'dateNextCall', value: txtDateNextCall.value, validType: 'date', isRequired: txtApplicationStatusID.value === 2 ? true : false, errorMessage: 'Дата отложенного действия не корректна или такой даты не существует.'},
            {fieldName: 'timeNextCall', value: txtTimeNextCall.value, validType: 'time', isRequired: false, errorMessage: 'Время отложенного действия не корректное или такого времени не сущесвует.'},
            {fieldName: 'applicationType', value: txtApplicationType.value, validType: 'notEmpty', isRequired: true, errorMessage: 'Тип заявки, обязательно должен быть указан.'},
            {fieldName: 'applicationSource', value: txtApplicationSource.value, validType: 'notEmpty', isRequired: true, errorMessage: 'Истояник заявки, обязательно должен быть указан.'},
        ])

        for (const item of validateData) { // Выводим ошибки валидации
            fieldsErrorAnumation(item.fieldName as FieldsErrorType)
            notify && notifyOpen(item.message, 'warning', 2000, notify)
        }

        if (validateData.length === 0) { // Сохраняем данные
            const formState = getCurrentFormState()
            saveGuard.runSave(() => actionSetApplicationCard(currentCardID.value, {
                actionForm: currentCardMode.value,
                dbName: formState.dbName,
            
                clientName: txtClientName.value || '',
                clientPhone: txtClientPhone.value,
                clientEmail: txtClientEmail.value,
                clientRating: txtClientRating.value,

                programNameID: txtProgramNameID.value || 0,
                programName: txtProgramName.value,
                
                programTypeID: txtProgramTypeID.value || 0,
                programType: txtProgramType.value,
                
                attestationTypeID: txtAttestationTypeID.value || 0,
            
                educationDateStart: txtEducationDateStart.value,
                educationDateEnd: txtEducationDateEnd.value,
            
                durationEducation: txtDurationEducation.value,
                volumeHours: txtVolumeHours.value,
                programSum: txtProgramSum.value,
            
                applicationStatusID: txtApplicationStatusID.value || 0,
                applicationTypeID: txtApplicationTypeID.value || 0,
                applicationSourceID: txtApplicationSourceID.value || 0,
            
                dateNextCall: txtDateNextCall.value,
                timeNextCall: txtTimeNextCall.value,
            
                applicationComment: txtApplicationComment.value,
            }), {isNew: currentCardMode.value === 'new', getSavedID: response => response.data?.applicationID}).then((res) => {
                if (res?.status === 200) {
                    const savedApplicationID = Number(res.data?.applicationID || currentCardID.value)
                    if (!Number.isSafeInteger(savedApplicationID) || savedApplicationID <= 0) {
                        notify && notifyOpen('Сервер не вернул идентификатор сохранённой заявки.', 'error', 2000, notify)
                        return
                    }

                    notify && notifyOpen('Данные успешно сохранены.', 'success', 1500, notify)
                    hasSavedChangesRef.current = true

                    if (isClosedModal) {
                        handleButtonOk && handleButtonOk()
                        return
                    }

                    currentCardID.setValue(savedApplicationID)
                    currentCardMode.setValue('edit')
                    applicationNumber.setValue(savedApplicationID)
                    savedFormSnapshotRef.current = createApplicationFormSnapshot(formState)
                    savedEducationSignatureRef.current = createEducationSignature(formState.durationEducation, formState.programTypeID, formState.educationDateStart)
                    isDisableBtnSave.setValue(true)
                    actionGetApplicationCard(savedApplicationID).then((response) => {
                        if (response.status === 200 && response.data) applyCardData(response.data)
                    })
                }
            }).catch((res) => {
                reportUnexpectedClientError(res, 'CardApplication.actionSetApplicationCard')
                notify && notifyOpen(res.message, 'error', 2000, notify)
            })
        }
    }
    /** Активация анимации ошибки в указанных полях */
    const fieldsErrorAnumation = useFieldsErrorAnimation<IFieldsError>(fieldsError.setValue)


    /** Функция по проверке номера телефона, в случае успеха даем вводить */
    const debouncePhone = useDebounce(applicationCheckPhoneNumber, 1500) // Запуск получения дат начала обучения и окончания обучения
    const handleChangePhone = (phoneNumber: string) => {
        const resultPhoneMask = phoneMask(phoneNumber)
        txtClientPhone.setValue(resultPhoneMask.data)
        debouncePhone(resultPhoneMask.data)
    }
    
    const debounceEduDate = useDebounce(getEducationDate, 1500) // Запуск получения дат начала обучения и окончания обучения
    const txtEducationDateStartOnChange = (value: string) => {
        txtEducationDateStart.setValue(value)
        if (!value) {
            txtEducationDateEnd.setValue('')
            return
        }
        debounceEduDate(value)
    } // Поле дата начала обучения
    const txtEducationDateEndOnChange = (value: string) => {txtEducationDateEnd.setValue(value); debounceEduDate(txtEducationDateStart.value)} // Поле дата окончания обучения

    /** Кнопка копирования текста в буфер обмена */
    const handleCopyToClipBoard = async (textToCopy: string) => {
        const textarea = document.createElement('textarea')
        textarea.value = textToCopy
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)

        notify && notifyOpen('Текст скопирован в буфер обмена!', 'success', 1500, notify)
    }
    /** Скачаивание файла с учебным планом */
    const downloadFile = async (fileName: string, fileUrl: string): Promise<void> => {
        try {
            const response = await actionDownloadFile(dataBaseName.value, fileUrl, `${fileName}.pdf`)
            if (!(response instanceof Blob)) {
                notify && notifyOpen(response.message, response.status >= 500 ? 'error' : 'warning', 4000, notify)
            }
        } catch (error) {
            reportUnexpectedClientError(error, 'CardApplication.actionDownloadFile')
            notify && notifyOpen('Не удалось скачать учебный план. Повторите попытку.', 'error', 4000, notify)
        }
    }

    // ================================================================================

    /** Свойства передаваемые в компоненту */
    const propsUI: ICardApplicationUI = {
        isOpen, cardMode: currentCardMode.value, 
        tabActive,

        applicationDbName: dbNameSelected.value,
        applicationNumber, applicationDateCreate, applicationIsRepeat,
        applicationOrigin: applicationOrigin.value,

        isLoading: isLoadingCard,
        isDisableBtnSave: {...isDisableBtnSave, value: isDisableBtnSave.value || saveGuard.isBlocked},

        getCardPage,
        handleOpenModalConfirm, handleOpenModalMailings,
        handleBtnSave, handleBtnClose,
    }

    /** Свойства передаваемые во вкладку - основные данные */
    const propsTabPrimaryUI: ICardApplicationTabPrimaryUI = {
        fieldsError, isFieldsDisabled, dbNameList: dbNameList.value, dbNameSelected,
        txtClientName, txtClientPhone, txtClientEmail, txtClientRating,
        txtProgramNameID, txtProgramName, txtProgramTypeID, txtProgramType, txtAttestationType, 
        txtEducationDateStart, txtEducationDateEnd, 
        txtDurationEducation, txtVolumeHours, txtProgramSum,
        txtApplicationStatusID, txtApplicationStatus, txtDateNextCall, txtTimeNextCall, 
        txtApplicationType, txtApplicationSource, txtApplicationComment,
        dictSelectorData, txtEducationDateStartOnChange, txtEducationDateEndOnChange,
        handleChangePhone, 
    }

    /** Свойства передаваемые во вкладку - Информация для оператора */
    const propsTabHelpUI: ICardApplicationTabHelpUI = {
        programsList, disciplinesPP, disciplinesPK, countDisciplineSectionPP, countDisciplineSectionPK,
        txtClientName, txtClientPhone, txtClientEmail, 
        txtAttestationType, txtProgramTypeID, 
        txtDurationEducation, txtVolumeHours, 
        txtEducationDateStart, txtEducationDateEnd, txtProgramSum,
        handleCopyToClipBoard, downloadFile
    }

    /** Свойства передаваемые во вкладку - Журнал изменений */
    const propsTabLogsUI: ICardApplicationTabLogsUI = {
        logsList
    }
    
    return <>
        <CardApplicationUI {...propsUI} />

        <ModalNotice {...modalNotice.value} />
        <ModalConfirm {...modalConfirm.value} />

        <ModalApplicationMailingsContainer {...modalMailings.value} />
    </>
}
